import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, thumbUrl, formatApiError } from "@/lib/api";
import { useFavourites } from "@/contexts/FavouritesContext";
import { useCart } from "@/contexts/CartContext";
import { toast } from "sonner";
import { Heart, ShoppingCart, Share2, Copy, Check } from "lucide-react";

function formatPrice(p, currency = "gbp") {
  try {
    return new Intl.NumberFormat("en-GB", { style: "currency", currency: currency.toUpperCase() }).format(p);
  } catch {
    return `£${Number(p).toFixed(2)}`;
  }
}

export default function FavouritesPage() {
  const { ids, removeFav, pruneTo } = useFavourites();
  const { addItem, addMany, hasItem } = useCart();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shareUrl, setShareUrl] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareShortlist = async () => {
    setSharing(true);
    try {
      const r = await api.post("/shortlists", { image_ids: ids });
      const url = `${window.location.origin}/shortlist/${r.data.id}`;
      setShareUrl(url);
      setCopied(false);
      try {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        toast.success("Share link copied to clipboard");
      } catch { /* clipboard unavailable */ }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSharing(false);
    }
  };

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Copied");
    } catch {
      toast.error("Couldn't copy — select the link manually");
    }
  };

  useEffect(() => {
    if (ids.length === 0) {
      setImages([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    api.get("/images", { params: { ids: ids.join(",") } })
      .then((r) => {
        if (cancelled) return;
        const found = Array.isArray(r.data) ? r.data : [];
        // Keep the buyer's original ordering
        const byId = Object.fromEntries(found.map((im) => [im.id, im]));
        setImages(ids.map((i) => byId[i]).filter(Boolean));
        if (found.length < ids.length) pruneTo(found.map((im) => im.id));
      })
      .catch(() => { /* keep whatever we have */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [ids, pruneTo]);

  const notInBasket = images.filter((im) => !hasItem(im.id));

  return (
    <div className="max-w-[1600px] mx-auto px-6 md:px-12 mt-8 pb-24" data-testid="favourites-page">
      <div className="mt-4 border-b border-white/10 pb-10 flex items-end justify-between flex-wrap gap-6">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-white/40">Your shortlist</p>
          <h1 className="font-serif text-5xl md:text-7xl font-light leading-none mt-3">
            Favourites <span className="pm-orange">♥</span>
          </h1>
          <p className="text-xs uppercase tracking-[0.2em] text-white/40 mt-6" data-testid="favourites-count">
            {images.length} {images.length === 1 ? "photo" : "photos"} saved
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {images.length > 0 && (
            <button
              onClick={shareShortlist}
              disabled={sharing}
              className="flex items-center gap-3 border border-white/25 px-6 py-4 text-xs uppercase tracking-[0.3em] hover:border-[var(--pm-orange)] transition disabled:opacity-50"
              data-testid="favourites-share"
            >
              <Share2 size={15} /> {sharing ? "Creating link…" : "Share shortlist"}
            </button>
          )}
          {notInBasket.length > 0 && (
            <button
              onClick={() => addMany(notInBasket)}
              className="flex items-center gap-3 bg-[var(--pm-orange)] text-black px-8 py-4 text-xs uppercase tracking-[0.3em] font-bold hover:opacity-90 transition"
              data-testid="favourites-add-all"
            >
              <ShoppingCart size={16} /> Add all to basket ({notInBasket.length})
            </button>
          )}
        </div>
      </div>

      {shareUrl && (
        <div className="mt-6 border border-[var(--pm-orange)]/40 bg-[var(--pm-orange)]/5 p-5 flex flex-wrap items-center gap-4" data-testid="favourites-share-box">
          <p className="text-xs uppercase tracking-[0.25em] text-white/50">Share link</p>
          <code className="text-sm pm-orange break-all flex-1 min-w-[200px]" data-testid="favourites-share-url">{shareUrl}</code>
          <div className="flex gap-3">
            <button
              onClick={copyShare}
              className="flex items-center gap-2 border border-white/25 px-4 py-2 text-xs uppercase tracking-[0.25em] hover:border-[var(--pm-orange)]"
              data-testid="favourites-share-copy"
            >
              {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Copied" : "Copy"}
            </button>
            <a
              href={`https://wa.me/?text=${encodeURIComponent(`Check out the photos I've shortlisted — ${shareUrl}`)}`}
              target="_blank" rel="noreferrer"
              className="flex items-center gap-2 border border-white/25 px-4 py-2 text-xs uppercase tracking-[0.25em] hover:border-[#25D366] hover:text-[#25D366]"
              data-testid="favourites-share-whatsapp"
            >
              WhatsApp
            </a>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-white/50 text-sm py-16 text-center uppercase tracking-[0.3em]">Loading…</p>
      ) : images.length === 0 ? (
        <div className="py-20 text-center" data-testid="favourites-empty">
          <Heart size={40} className="mx-auto text-white/20" />
          <p className="text-white/50 text-sm mt-6">No favourites yet — tap the heart on any photo to shortlist it here.</p>
          <Link to="/events" className="inline-block mt-8 border border-white/25 px-8 py-4 text-xs uppercase tracking-[0.3em] hover:border-[var(--pm-orange)]">
            Browse events
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-10">
          {images.map((img, idx) => (
            <div
              key={img.id}
              className="relative gallery-card block aspect-[4/5] fade-up"
              style={{ animationDelay: `${idx * 40}ms` }}
              data-testid={`favourite-card-${img.id}`}
            >
              <Link to={`/image/${img.id}`} className="block w-full h-full">
                <img src={thumbUrl(img.id)} alt={img.title} loading="lazy" className="w-full h-full object-cover" />
                <div className="reveal">
                  <div>
                    <h3 className="font-serif text-2xl text-white leading-tight">{img.title}</h3>
                    <p className="text-xs uppercase tracking-[0.2em] text-white/60 mt-2">{formatPrice(img.price, img.currency)}</p>
                  </div>
                  <span className="text-xs uppercase tracking-[0.2em] text-white border border-white/40 px-3 py-2">View</span>
                </div>
              </Link>
              <button
                onClick={(e) => { e.preventDefault(); removeFav(img.id); }}
                className="absolute top-3 left-3 z-10 p-2.5 bg-[var(--pm-orange)] text-black transition hover:opacity-80"
                data-testid={`favourite-remove-${img.id}`}
                aria-label="Remove from favourites"
              >
                <Heart size={16} fill="currentColor" />
              </button>
              <button
                onClick={(e) => { e.preventDefault(); addItem(img); }}
                className={`absolute top-3 right-3 z-10 text-xs uppercase tracking-[0.2em] font-bold px-3 py-2 transition ${
                  hasItem(img.id)
                    ? "bg-[var(--pm-orange)] text-black"
                    : "bg-black/70 text-white border border-white/40 hover:bg-[var(--pm-orange)] hover:text-black hover:border-[var(--pm-orange)]"
                }`}
                data-testid={`favourite-add-to-cart-${img.id}`}
              >
                {hasItem(img.id) ? "✓ In basket" : "+ Basket"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
